import {
  BaseField,
  type DefaultMarketAccounts,
  type FieldData,
  fieldsMessages,
  FieldValidationLevel,
  type FieldValidationResult,
  FieldValidationType,
  type MarketAccount,
} from '@talos/kyoko';
import { compact, isEqual, uniq } from 'lodash-es';
import { shouldClearAggregation } from '../../OMS/NewOrder/utils';
import { getOneAccountPerMarket } from '../../OMS/utils';
import { resolveAggregationMarketSelectorValue } from '../presets/resolveAggregationMarketSelectorValue';
import {
  AvailabilityEnum,
  type MarketSelectorItem,
  type MarketSelectorPreset,
  type MarketSelectorValue,
} from '../types';
import { containsMultipleSelectionsPerMarket } from '../useMarketSelector';
import { getItemWithHighestAvailability, isMarketSelectorItemSelectable } from './items';

export interface OrderMarketsFieldData extends FieldData<MarketSelectorValue> {
  /** The available items to select from. Undefined means that we do not yet know what is selectable: we are loading. */
  items: MarketSelectorItem[] | undefined;

  /** Starts as false. Is set to true after initialize() is called. */
  initialized: boolean;
}

interface InitSettings {
  /**
   * If true, will allow disabled selections to be initialized. Otherwise, Disabled items are filtered out.
   * An example usage if when modifying an order. In that case, even if a market is currently disabled, you do not
   * want to remove it from the order.
   */
  allowDisabledSelections?: boolean;
  /** If true, the field should look to _default_ to empty */
  defaultToEmpty?: boolean;
  /**
   * Used to provide a set of default market accounts. When defaulting, if there are several possible accounts per market, the field
   * will look into this data set to see if there is any provided default account for the market to use.
   */
  defaultMarketAccounts?: DefaultMarketAccounts;
  defaultAggregationId?: string;
  aggregationPresets?: MarketSelectorPreset[];
}

export const EMPTY_MARKET_SELECTOR_VALUE: MarketSelectorValue = { type: 'custom', selections: [] };

export class OrderMarketsField extends BaseField<OrderMarketsFieldData, MarketSelectorValue> {
  constructor(initial?: Partial<OrderMarketsFieldData>) {
    super({
      name: 'OrderMarketsField',
      value: { type: 'custom', selections: [] },
      isRequired: true,
      isTouched: false,
      isDisabled: false,
      isVisible: true,
      placeholder: 'Select...',
      errors: [],
      items: undefined,
      initialized: false,
      isFocused: false,
      ...initial,
    });

    this.itemsMap = this.items ? new Map(this.items?.map(item => [item.name, item])) : undefined;
  }

  get items() {
    return this.data.items;
  }

  private itemsMap: Map<string, MarketSelectorItem> | undefined = undefined;

  get value(): MarketSelectorValue {
    return this.data.value ?? EMPTY_MARKET_SELECTOR_VALUE;
  }

  get selections() {
    return this.value.selections;
  }

  get aggregation() {
    return this.value?.type === 'preset' ? this.value.presetID : undefined;
  }

  get initialized() {
    return this.data.initialized;
  }

  public override setFocused(isFocused: boolean): BaseField<OrderMarketsFieldData, MarketSelectorValue> {
    return this.updateData({ isFocused });
  }

  initialize({
    desiredValue,
    items,
    settings,
  }: {
    desiredValue: MarketSelectorValue | undefined;
    items: MarketSelectorItem[];
    settings?: InitSettings;
  }): OrderMarketsField {
    if (settings?.defaultToEmpty && desiredValue == null) {
      return this.updateData({
        value: { type: 'custom', selections: [] },
        items,
        initialized: true,
      }).invariantCheck();
    }

    const desiredSelections = desiredValue?.selections ? uniq(desiredValue?.selections) : undefined;
    const { availabilities, selectableMarketAccounts } = organizeMarketSelectorItemsByAvailability(items);

    if (desiredSelections) {
      // The user is initializing the order markets with some selections to make.
      const validDesiredSelections = desiredSelections.filter(s => {
        const availability = availabilities.get(s);
        if (availability == null) {
          return false;
        }

        if (settings?.allowDisabledSelections) {
          return availability >= AvailabilityEnum.Disabled; // allow disabled: >=
        }

        return availability > AvailabilityEnum.Disabled; // do not allow disabled: >
      });
      const validDesiredPreset =
        desiredValue?.type === 'preset' && validDesiredSelections.length > 0 ? desiredValue.presetID : undefined;

      // If there is a desired preset, set it if it is relevant. If not, just set the desired selections
      const value: MarketSelectorValue = validDesiredPreset
        ? { type: 'preset', presetID: validDesiredPreset, selections: validDesiredSelections }
        : {
            type: 'custom',
            selections: validDesiredSelections,
          };
      return this.updateData({ value, items, initialized: true }).invariantCheck();
    }

    const fromDefaultAggregation = resolveAggregationMarketSelectorValue({
      aggregationId: settings?.defaultAggregationId,
      aggregationPresets: settings?.aggregationPresets,
      items,
    });
    if (fromDefaultAggregation) {
      return this.initialize({ desiredValue: fromDefaultAggregation, items, settings });
    }

    // Else, down here we are initializing the field without any desired selections. So we just do regular defaulting.
    const defaultSelections = getOneAccountPerMarket(selectableMarketAccounts, settings?.defaultMarketAccounts).map(
      ma => ma.Name
    );
    return this.updateData({
      value: { type: 'custom', selections: defaultSelections },
      items,
      initialized: true,
    }).invariantCheck();
  }

  updateValue(value: MarketSelectorValue): OrderMarketsField {
    // Do some sanity data cleaning here for things we know for certain - for example, each selection must be unique
    const cleanedValue = { ...value, selections: uniq(value.selections) };
    const updated = this.updateData({ value: cleanedValue });
    return updated.invariantCheck();
  }

  setTouched(isTouched: boolean): OrderMarketsField {
    const updated = this.updateData({ isTouched });
    return updated.invariantCheck();
  }

  setIsVisible(isVisible: boolean): OrderMarketsField {
    const updated = this.updateData({ isVisible });
    return updated.invariantCheck();
  }

  setIsRequired(isRequired: boolean): OrderMarketsField {
    const updated = this.updateData({ isRequired });
    return updated.invariantCheck();
  }

  setDisabled(isDisabled: boolean): OrderMarketsField {
    const updated = this.updateData({ isDisabled });
    return updated.invariantCheck();
  }

  updateItems(newItems: MarketSelectorItem[]): OrderMarketsField {
    const { availabilities } = organizeMarketSelectorItemsByAvailability(newItems);

    // All items disabled or above in terms of availability are relevant to the user. This means that we can be keeping Disabled accounts around at this stage.
    const newSelections = this.selections.filter(selection => {
      const availability = availabilities.get(selection) ?? AvailabilityEnum.Excluded;
      return availability >= AvailabilityEnum.Disabled;
    });

    let newField = new OrderMarketsField({ ...this.data, items: newItems });
    if (!isEqual(this.selections, newSelections)) {
      // We need to make a change to the selections after the avaiable items changed.
      let newValue: MarketSelectorValue = this.value
        ? { ...this.value, selections: newSelections }
        : { type: 'custom', selections: [] };

      const clearAggregation =
        newValue.type === 'preset' &&
        shouldClearAggregation({
          newMarkets: newSelections,
          oldMarkets: this.selections,
          items: newItems,
        });

      if (clearAggregation) {
        newValue = { type: 'custom', selections: newValue.selections };
      }

      newField = newField.updateValue(newValue);
    }

    return newField.invariantCheck();
  }

  hasMultipleSelectionsPerMarket(): boolean {
    if (!this.selections || !this.itemsMap) {
      return false;
    }

    return containsMultipleSelectionsPerMarket(this.selections, this.itemsMap);
  }

  /**
   * In some cases, the OrderMarketsField can be used to select multiple selections per market. That is driven by dynamic options,
   * such as Unified Liquidity being enabled. In those cases, when those options are then disabled, this function lets you easily
   * remove any multiple selections per market, going back to the default behavior (one selection per market).
   */
  removeAnyMultipleSelectionsPerMarket(): OrderMarketsField {
    if (!this.items) {
      return this;
    }
    const newSelections = removeAnyMultipleSelectionsPerMarket({ selections: this.selections, items: this.items });
    const nothingChanged = newSelections.length === this.selections.length;
    if (nothingChanged) {
      return this;
    }

    return this.updateValue({ ...this.value, selections: newSelections });
  }

  /** Just loops over the selections list and return the MarketSelectorItem for each */
  getSelectedMarketSelectorItems(): MarketSelectorItem[] {
    return compact(this.selections.map(selection => this.itemsMap?.get(selection)));
  }

  validate(): OrderMarketsField {
    // other fields accept rules as a parameter here. we dont have that yet, but we might...
    return this.invariantCheck();
  }

  private invariantCheck(): OrderMarketsField {
    const errors: FieldValidationResult[] = this.data.errors.filter(e => e.type === FieldValidationType.Rule);

    const missingValue = this.data.value == null || this.data.value.selections.length === 0;
    if (this.data.isRequired && missingValue) {
      errors.push({
        message: fieldsMessages.dataNameIsRequired,
        values: { dataName: this.data.name },
        level: FieldValidationLevel.Error,
      });
    }

    return this.updateData({ errors });
  }

  private updateData(data: Partial<OrderMarketsFieldData>): OrderMarketsField {
    return new OrderMarketsField({ ...this.data, ...data });
  }
}

// Just makes the items array easier to make use of
export function organizeMarketSelectorItemsByAvailability(items: MarketSelectorItem[]) {
  const availabilities = new Map<string, AvailabilityEnum>();
  const selectableMarketAccounts: MarketAccount[] = [];
  for (const item of items) {
    if (item.marketAccount == null) {
      continue;
    }

    availabilities.set(item.marketAccount.Name, item.availability.availability);
    if (isMarketSelectorItemSelectable(item)) {
      selectableMarketAccounts.push(item.marketAccount);
    }
  }

  return { availabilities, selectableMarketAccounts };
}

/**
 * Given the information of current selections and their items ("metadata"),
 * goes through all selections and if there are multiple selections per any market, will only keep one selection in that market.
 *
 * Returns a new array of selection: string[] for you to use directly.
 *
 * If there are multiple selections within a market, the selection with the highest availability score will be kept.
 *
 * This is its own function so it can more easily be reused.
 */
export function removeAnyMultipleSelectionsPerMarket({
  selections,
  items,
}: {
  selections: string[];
  items: MarketSelectorItem[];
}): string[] {
  const itemsMap = new Map(items.map(item => [item.name, item]));
  const itemsByMarket = new Map<string, MarketSelectorItem[]>();

  // Organize all selections into an array of items by market
  for (const selection of selections) {
    const item = itemsMap.get(selection);
    if (!item) {
      continue;
    }

    const itemsInMarketArr = itemsByMarket.get(item.market.Name) ?? [];
    itemsInMarketArr.push(item);
    itemsByMarket.set(item.market.Name, itemsInMarketArr);
  }

  // For each array of selections _per market_, only keep one.
  const itemsToKeep: string[] = [];

  itemsByMarket.forEach(items => {
    const itemToKeep = items.length === 1 ? items.at(0)?.name : getItemWithHighestAvailability(items)?.name;
    if (itemToKeep) {
      itemsToKeep.push(itemToKeep);
    }
  });

  return itemsToKeep;
}
